import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { createSoloSession, SessionError } from '../../lib/session'
import CreatorAuthGate from '../auth/CreatorAuthGate'
import { useAuth } from '../auth/AuthProvider'

const NAME_KEY = 'dayone.displayName'

const WHAT_HAPPENS = [
  'Scout helps you pick one real workflow worth automating',
  'Archivist finds the decisions nobody has written down',
  'Builder turns all of it into a prompt you can use tomorrow morning',
]

export default function SoloStartPage() {
  const navigate = useNavigate()
  const { user, loading: authLoading, verified } = useAuth()
  const [name, setName] = useState(() => {
    try {
      return localStorage.getItem(NAME_KEY) || ''
    } catch {
      return ''
    }
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // A solo session lives on the visitor's account, so the sign-in gate comes before
  // the name field. An already-verified visitor never sees it.
  const [authComplete, setAuthComplete] = useState(false)
  const showGate = !verified && !authComplete

  const handleAuthReady = useCallback(() => setAuthComplete(true), [])

  // A signed-in profile is the better default, but it arrives after first paint.
  // Functional update so a name already typed or restored is never overwritten.
  useEffect(() => {
    const profileName = user?.displayName
    if (profileName) setName((current) => current || profileName)
  }, [user?.displayName])

  async function handleStart(e: React.FormEvent) {
    e.preventDefault()
    const displayName = name.trim()
    if (!displayName) return
    setLoading(true)
    setError('')
    try {
      const result = await createSoloSession(displayName)
      try {
        localStorage.setItem(NAME_KEY, displayName)
      } catch {
        // Private browsing: the prefill is a convenience, not a requirement.
      }
      sessionStorage.setItem('participantId', result.participantId)
      sessionStorage.setItem('displayName', displayName)
      navigate(`/session/${result.sessionId}`)
    } catch (err) {
      console.error('Solo session creation failed:', err)
      // The server is the authority on who may start a session. If it says the
      // account is not good enough, show the gate again rather than a dead form.
      if (
        err instanceof SessionError &&
        (err.code === 'SIGN_IN_REQUIRED' || err.code === 'EMAIL_NOT_VERIFIED')
      ) {
        setAuthComplete(false)
        setError(err.message)
        return
      }
      // 400 (bad name) and 429 (daily limit, or we are at capacity) carry an
      // explanation written for the visitor. Anything else is on us.
      setError(
        err instanceof SessionError && (err.status === 400 || err.status === 429)
          ? err.message
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-stone flex items-center justify-center p-4">
      <div className="card card-shadow max-w-lg w-full p-7 sm:p-8 space-y-6">
        <div>
          <p className="mono-label text-teal mb-2">Solo session / 25 min</p>
          <h1 className="hl text-3xl text-navy mb-3">Start a Solo Session</h1>
          <p className="descriptor text-navy/75 leading-snug">
            Just you and three AI teammates, working on a workflow you own.
          </p>
        </div>

        <div className="bg-paper border-2 border-navy p-4">
          <p className="mono-label text-teal border-b border-navy/30 pb-2 mb-3">
            What happens
          </p>
          <ol className="text-sm text-navy/80">
            {WHAT_HAPPENS.map((step, i) => (
              <li key={step} className="flex items-start gap-3 py-1.5">
                <span className="mono-label text-amber pt-0.5 tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="leading-snug">{step}</span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-navy/72 pt-3 leading-relaxed border-t border-navy/20 mt-2">
            You leave with a downloadable prompt pack, and the summary lands in your
            inbox when you finish.
          </p>
        </div>

        {authLoading ? (
          <p className="mono-label text-navy/70 text-center">Loading</p>
        ) : showGate ? (
          <div className="space-y-3">
            <CreatorAuthGate onReady={handleAuthReady} />
            {error && <p className="text-brick text-sm border-l-2 border-brick pl-3">{error}</p>}
          </div>
        ) : (
          <form onSubmit={handleStart} className="space-y-4">
            <div>
              <label className="block mono-label text-navy/70 mb-1.5" htmlFor="solo-name">
                What should we call you?
              </label>
              <input
                id="solo-name"
                required
                maxLength={40}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full border-2 border-navy/55 focus:border-navy px-3.5 py-2.5 text-base bg-paper outline-none"
                autoFocus
              />
            </div>
            {error && <p className="text-brick text-sm border-l-2 border-brick pl-3">{error}</p>}
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="btn btn-amber w-full"
            >
              {loading ? 'Starting...' : (
                <>
                  Start <ArrowRight size={18} />
                </>
              )}
            </button>
            <p className="text-xs text-navy/70 leading-relaxed">
              Free. The session saves to your account, so you can reopen it from My
              Sessions whenever you want.
            </p>
          </form>
        )}

        <p>
          <button
            onClick={() => navigate('/')}
            className="mono-label text-navy/70 hover:text-navy transition cursor-pointer"
          >
            Back to the homepage
          </button>
        </p>
      </div>
    </div>
  )
}
